import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import type { BackgroundJob } from "@/apis/gql/types";
import { useJobStatusSubscription } from "@/queries/jobs/subscription";

type BackgroundJobsContextType = {
  backgroundJobs: BackgroundJob[];
  clearBackgroundJobs: () => void;
};

const BackgroundJobsContext = createContext<BackgroundJobsContextType>({
  backgroundJobs: [],
  clearBackgroundJobs: () => {},
});

export const BackgroundJobsProvider = ({ children }: PropsWithChildren) => {
  const { data } = useJobStatusSubscription();

  const [backgroundJobs, setBackgroundJobs] = useState<BackgroundJob[]>([]);

  useEffect(() => {
    if (data) {
      const { jobStatus } = data;

      if (!jobStatus) return;

      setBackgroundJobs((prev) => {
        const index = prev.findIndex((job) => job.jobId === jobStatus.jobId);

        if (index >= 0) {
          return [...prev.slice(0, index), jobStatus, ...prev.slice(index + 1)];
        }
        return [...prev, jobStatus];
      });
    }
  }, [data]);

  const clearBackgroundJobs = () => {
    setBackgroundJobs([]);
  };

  return (
    <BackgroundJobsContext.Provider
      value={{ backgroundJobs, clearBackgroundJobs }}
    >
      {children}
    </BackgroundJobsContext.Provider>
  );
};

export function useBackgroundJobs<D = Record<string, unknown>, T = unknown>({
  pick,
}: { pick: string[] }) {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error(
      "useBackgroundJobs must be used within a BackgroundJobsProvider",
    );
  }

  const filteredJobs = context.backgroundJobs.filter((job) =>
    pick.includes(job.jobName),
  ) as BackgroundJob<D, T>[];

  return {
    backgroundJobs: filteredJobs,
    clearBackgroundJobs: context.clearBackgroundJobs,
  };
}

export function useClearBackgroundJobs() {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error(
      "useClearBackgroundJobs must be used within a BackgroundJobsProvider",
    );
  }

  return context.clearBackgroundJobs;
}
